// 조직 멤버(OrganizationMember) 공통 판정·표시 — 여러 화면이 같은 규칙을 쓰도록 한 곳에 모은다.
//
// 배경(2026-08-11): 신원=Member / 권한=CompanyMember / 로그인 계정=User(선택)로 나뉜 뒤
// **user가 없는 Member가 정상 상태**가 됐는데(외부 연락처·결재자 프로필), 화면 곳곳이 옛 전제
// ("user는 늘 있다")를 유지하고 있었다. 그 결과:
//  - 이름을 `user.name`에서만 읽어 외부 연락처가 '-'로 표시되고 이름 검색도 실패했다.
//  - 조직 구성원만 대상이어야 할 목록(현장 점검 담당자 후보)에 외부 연락처가 섞였다.
// 규칙을 파일마다 손으로 적으면 한쪽만 고쳐져 다시 갈라지므로 여기 단일 정본을 둔다.

import type { OrganizationMember } from './organizations';

/**
 * 조직 구성원 = 활성 역할 배정 중 **구성원 자격을 주는 역할**(`CompanyRole.countsAsMember`)이 하나라도 있는 CompanyMember 행.
 *
 * 판정은 서버가 한다(company-rbac P7-b-1, 2026-09-25 — `getMembers`가 행마다 `countsAsMember`를 싣는다, 규칙 정본
 * `apps/server/src/permissions/member-kind.ts`). web은 그 값만 읽는다 — 역할 카탈로그를 따로 받아 계산하지 않는다(`isOwner`와
 * 같은 자세). 외부 인원(현장 담당자·외부 결재자 — 계정 없이 토큰으로 일하는 사람)은 `site-staff` 역할만 가져 false이고,
 * 소유자가 현장 담당자로도 등록된 겸직은 `owner`도 있어 true다(서버가 `some`으로 판정 — `every`가 아니다).
 *
 * 옛 축 `role != null`(2026-08-02 사용자 확정 「role null = 외부 contact」)은 서버 `role` 컬럼이 병행 가동 뒤 P7-b-2에서
 * 지워지므로 여기서는 더 이상 보지 않는다. 값이 없으면(옛 형상 객체) 구성원이 아니다 — 외부인이 인원에 섞이는 쪽이 구성원이
 * 잠시 안 보이는 쪽보다 나쁘다(전자는 2026-08-02 실사고). 조직 구성원만 대상인 목록은 반드시 이 술어를 지날 것.
 */
export function isOrganizationMember(member: OrganizationMember): boolean {
  return member.countsAsMember === true;
}

/**
 * 표시 이름 — **Member가 신원의 정본**이므로 `member.name`이 우선이다.
 * `user.name`은 로그인 계정 쪽 사본이라 계정이 없으면 통째로 없다(서버 getMembers가
 * `user: member.userId ? {...} : null`로 내려보낸다).
 *
 * 이름이 어느 쪽에도 없을 때만 fallback('-')을 쓴다. 검색·정렬에는 fallback이 섞이면
 * 안 되므로 `memberSearchName`을 쓸 것.
 */
export function memberDisplayName(
  member: Pick<OrganizationMember, 'member' | 'user'>,
  fallback = '-',
): string {
  const name = member.member?.name?.trim() || member.user?.name?.trim();
  return name || fallback;
}

/** 검색·정렬용 이름 — 없으면 빈 문자열(자리표시 '-'가 검색어에 걸리지 않게). */
export function memberSearchName(
  member: Pick<OrganizationMember, 'member' | 'user'>,
): string {
  return memberDisplayName(member, '');
}
